import { queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { identityKeys, type Identity, type IdentityFilters } from './entity';

export type { IdentityFilters } from './entity';

/**
 * Sender / reply-to identities for a workspace.
 *
 * Read-only lists. `useIdentities` is the unfiltered read used by filters that
 * scope by "from address" (e.g. the message-library page); the filtered reads
 * below back the email-settings screens, which need the same server-side
 * filtering the Ember pages relied on. There is no detail endpoint — the
 * endpoint only serves lists — so every read here is a list read.
 */

const STALE_TIME = 5 * 60_000;

/**
 * The email-settings lists are always-fresh: an identity created or verified in
 * another tab must show up on the next visit, not after a stale window.
 */
const FILTERED_STALE_TIME = 0;

async function getIdentities(workspaceId: number): Promise<Identity[]> {
  const response = await hydraUiApi.identities.list({
    params: { path: { environment_id: String(workspaceId) } },
  });
  return response.identities;
}

/** @public */
export async function getFilteredIdentities(workspaceId: number, filters: IdentityFilters): Promise<Identity[]> {
  const response = await hydraUiApi.identities.list({
    params: { path: { environment_id: String(workspaceId) }, query: filters },
  });
  return response.identities;
}

const getIdentitiesQueryOptions = (workspaceId: number) =>
  queryOptions({
    // The unfiltered read shares the `…/identity/list` prefix the filtered
    // slots hang off, so one invalidation reaches every identity list.
    queryKey: identityKeys.lists(workspaceId),
    queryFn: () => getIdentities(workspaceId),
    staleTime: STALE_TIME,
  });

/** @public */
export const getFilteredIdentitiesQueryOptions = (workspaceId: number, filters: IdentityFilters) =>
  queryOptions({
    queryKey: identityKeys.list(workspaceId, filters),
    queryFn: () => getFilteredIdentities(workspaceId, filters),
    staleTime: FILTERED_STALE_TIME,
  });

interface UseIdentitiesOptions {
  queryConfig?: QueryConfig<typeof getIdentitiesQueryOptions>;
}

/** @public */
export const useIdentities = ({ queryConfig }: UseIdentitiesOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({
    ...getIdentitiesQueryOptions(workspaceId),
    ...queryConfig,
  });
};

interface UseFilteredIdentitiesOptions {
  queryConfig?: QueryConfig<typeof getFilteredIdentitiesQueryOptions>;
}

const EMAIL_IDENTITY_FILTERS: IdentityFilters = { type: 'email' };
const IN_USE_IDENTITY_FILTERS: IdentityFilters = { in_use: true };

/** The read `useEmailIdentities` makes, for loading it into a client ahead of a render. */
export const getEmailIdentitiesQueryOptions = (workspaceId: number) =>
  getFilteredIdentitiesQueryOptions(workspaceId, EMAIL_IDENTITY_FILTERS);

/** Email-channel identities only. @public */
export const useEmailIdentities = ({ queryConfig }: UseFilteredIdentitiesOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({
    ...getEmailIdentitiesQueryOptions(workspaceId),
    ...queryConfig,
  });
};

/**
 * Email identities for surfaces that can render before the workspace id is in
 * the URL (the onboarding rail): no workspace means no fetch rather than a
 * throw. Shares the cache slot with `useEmailIdentities`.
 *
 * @public
 */
export const useEmailIdentitiesOptional = () => {
  const workspaceId = useWorkspaceIdFromCurrentUrl();
  const validWorkspaceId = workspaceId != null && workspaceId > 0 ? workspaceId : null;
  return useQuery({
    queryKey:
      validWorkspaceId != null
        ? identityKeys.list(validWorkspaceId, EMAIL_IDENTITY_FILTERS)
        : ['identity', 'no-workspace'],
    queryFn:
      validWorkspaceId != null ? () => getFilteredIdentities(validWorkspaceId, EMAIL_IDENTITY_FILTERS) : skipToken,
    staleTime: FILTERED_STALE_TIME,
  });
};

/** Identities a message currently sends from, across channels. @public */
export const useInUseIdentities = ({ queryConfig }: UseFilteredIdentitiesOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({
    ...getFilteredIdentitiesQueryOptions(workspaceId, IN_USE_IDENTITY_FILTERS),
    ...queryConfig,
  });
};

/** @public */
export const getDynamicSenderNamesQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: [...identityKeys.all(workspaceId), 'dynamic-sender-names'],
    queryFn: async () => {
      const response = await hydraUiApi.identities.getDynamicSenderNames({
        params: { path: { environment_id: String(workspaceId) } },
      });
      return response.names;
    },
    staleTime: STALE_TIME,
  });
